import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { BrandLogo } from "./BrandLogo";
import {
    LayoutDashboard, Sparkles,
    Bell, ClipboardList, Calculator as CalcIcon, Moon, Sun, LogOut, Menu, X, Settings as SettingsIcon,
    Target, Gamepad2, Newspaper, CalendarRange, Repeat, ShieldCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import CaesarWidget from "./CaesarWidget";
import AssistantSparkIcon from "./AssistantSparkIcon";
import { api } from "../lib/api";

const links = [
    { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard },
    { to: "/planning", key: "nav_planning", icon: CalendarRange },
    { to: "/subscriptions", key: "nav_subscriptions", icon: Repeat },
    { to: "/insights", key: "nav_insights", icon: Sparkles },
    { to: "/action-plan", key: "nav_action_plan", icon: ClipboardList },
    { to: "/goals", key: "nav_goals", icon: Target },
    { to: "/calculator", key: "nav_calculator", icon: CalcIcon },
    { to: "/arcade", key: "nav_arcade", icon: Gamepad2 },
    { to: "/blog", key: "nav_blog", icon: Newspaper },
];

// Bottom nav — split into two sides around a centered raised chat button
// Mobile bottom nav — horizontal scrollable list of ALL nav destinations
// (order matches desktop sidebar). Caesar sits as a floating spark button on
// the right so the strip stays uncluttered.
const mobileNavAll = [
    { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard },
    { to: "/planning", key: "nav_planning", icon: CalendarRange },
    { to: "/subscriptions", key: "nav_subscriptions", icon: Repeat },
    { to: "/insights", key: "nav_insights", icon: Sparkles },
    { to: "/action-plan", key: "nav_action_plan", icon: ClipboardList },
    { to: "/goals", key: "nav_goals", icon: Target },
    { to: "/arcade", key: "nav_arcade", icon: Gamepad2 },
    { to: "/blog", key: "nav_blog", icon: Newspaper },
    { to: "/calculator", key: "nav_calculator", icon: CalcIcon },
    { to: "/settings", key: "nav_settings", icon: SettingsIcon },
];

const AppLayout = ({ children, title }) => {
    const { user, language, theme, updateSettings, logout } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!user) return;
        let mounted = true;
        // /alerts returns {alerts, unread_count} — powered by the Wave-1 alerts engine.
        api.get("/alerts").then((r) => {
            if (mounted) setUnread(r?.data?.unread_count || 0);
        }).catch(() => {});
        return () => { mounted = false; };
    }, [user, location.pathname]);

    if (!user) return null;

    const NavItems = ({ onClick }) => (
        <>
            {links.map((l) => (
                <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.to.replace("/", "")}`}
                    onClick={onClick}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                            isActive
                                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                                : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--foreground))]"
                        }`
                    }
                >
                    <l.icon className="w-4 h-4" />
                    {t(language, l.key)}
                </NavLink>
            ))}
        </>
    );

    return (
        <div className="min-h-screen flex bg-[hsl(var(--background))]">
            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                <button
                    data-testid="brand-home-link"
                    onClick={() => navigate("/dashboard")}
                    className="px-6 py-7 flex items-center gap-3 text-[hsl(var(--primary))]"
                >
                    <BrandLogo size={30} />
                    <span className="font-display font-bold text-base tracking-tight text-[hsl(var(--foreground))]">Vive a lo Grande</span>
                </button>
                <nav className="px-3 flex-1 space-y-1">
                    <NavItems />
                </nav>
                {/* Settings link — sits directly above the log-off / profile row */}
                <div className="px-3 pb-2 space-y-1">
                    {user.is_admin && (
                        <NavLink
                            to="/admin"
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                                        : "text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--foreground))]"
                                }`
                            }
                        >
                            <ShieldCheck className="w-4 h-4" />
                            <span>Admin</span>
                        </NavLink>
                    )}
                    <NavLink
                        to="/settings"
                        data-testid="nav-settings"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                isActive
                                    ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                                    : "text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--foreground))]"
                            }`
                        }
                    >
                        <SettingsIcon className="w-4 h-4" />
                        <span>{t(language, "nav_settings")}</span>
                    </NavLink>
                </div>
                <div className="p-4 border-t border-[hsl(var(--border))] flex items-center gap-3">
                    <button
                        data-testid="sidebar-profile-settings"
                        onClick={() => navigate("/settings")}
                        className="flex items-center gap-3 flex-1 min-w-0 hover:bg-[hsl(var(--secondary))] rounded-lg p-1 -m-1 transition-colors"
                    >
                        <div className="w-9 h-9 rounded-full bg-[hsl(var(--secondary))] grid place-items-center overflow-hidden">
                            {user.picture ? (
                                <img src={user.picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-xs font-bold text-[hsl(var(--primary))]">
                                    {user.name?.[0]?.toUpperCase() || "?"}
                                </span>
                            )}
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                            <div className="text-sm font-semibold truncate">{user.name}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user.email}</div>
                        </div>
                    </button>
                    <button
                        data-testid="logout-button"
                        onClick={logout}
                        className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]"
                        aria-label="Logout"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </aside>

            {/* Mobile drawer */}
            {drawerOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
                    <div className="relative w-72 max-w-[80vw] bg-[hsl(var(--card))] flex flex-col animate-fade-up">
                        <div className="px-6 py-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <BrandLogo size={28} />
                                <span className="font-display font-bold">Vive a lo Grande</span>
                            </div>
                            <button onClick={() => setDrawerOpen(false)} className="p-2">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <nav className="px-3 flex-1 space-y-1 overflow-y-auto">
                            <NavItems onClick={() => setDrawerOpen(false)} />
                        </nav>
                        <button
                            onClick={() => { setDrawerOpen(false); navigate("/settings"); }}
                            className="mx-4 mb-2 flex items-center gap-2 px-4 py-3 rounded-xl bg-[hsl(var(--secondary))]/70 text-sm font-medium"
                        >
                            <SettingsIcon className="w-4 h-4" /> {t(language, "nav_settings")}
                        </button>
                        <button
                            onClick={logout}
                            className="mx-4 mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-[hsl(var(--secondary))] text-sm font-medium"
                        >
                            <LogOut className="w-4 h-4" /> {t(language, "logout")}
                        </button>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 flex flex-col min-w-0 pb-24 md:pb-0">
                <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-[hsl(var(--border))]">
                    <div className="flex items-center justify-between px-4 md:px-10 h-16 gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <button
                                data-testid="mobile-menu-toggle"
                                onClick={() => setDrawerOpen(true)}
                                className="md:hidden p-2 rounded-lg hover:bg-[hsl(var(--secondary))]"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                            <h1 className="font-display font-bold text-lg md:text-2xl tracking-tight text-[hsl(var(--foreground))] truncate text-center flex-1" data-testid="page-title">
                                {title}
                            </h1>
                        </div>
                        <div className="flex items-center gap-1 md:gap-2 shrink-0">
                            {/* Notification bell */}
                            <button
                                data-testid="header-notifications"
                                onClick={() => navigate("/notifications")}
                                className="relative p-2 rounded-full hover:bg-[hsl(var(--secondary))] transition-colors"
                                aria-label="Notifications"
                            >
                                <Bell className="w-4 h-4 text-[hsl(var(--foreground))]" />
                                {unread > 0 && (
                                    <span
                                        data-testid="header-notifications-badge"
                                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--accent))] text-white text-[9px] font-bold grid place-items-center leading-none"
                                    >
                                        {unread > 9 ? "9+" : unread}
                                    </span>
                                )}
                            </button>
                            <Button
                                data-testid="lang-toggle"
                                variant="ghost"
                                size="sm"
                                className="pill text-xs font-semibold tracking-[0.2em] h-9"
                                onClick={() => updateSettings({ language: language === "es" ? "en" : "es" })}
                            >
                                {language === "es" ? "EN" : "ES"}
                            </Button>
                            <Button
                                data-testid="theme-toggle"
                                variant="ghost"
                                size="icon"
                                className="pill h-9 w-9"
                                onClick={() => updateSettings({ theme: theme === "dark" ? "light" : "dark" })}
                            >
                                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-4 md:px-10 py-6 md:py-8 pb-24 md:pb-8">
                    <div className="max-w-[1400px] mx-auto w-full">{children}</div>
                </main>
            </div>

            {/* Mobile bottom nav — horizontal scrollable strip with all destinations */}
            <nav
                data-testid="mobile-bottom-nav"
                className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[hsl(var(--card))]/95 backdrop-blur border-t border-[hsl(var(--border))] mobile-nav-safe"
            >
                <div className="relative flex items-stretch">
                    <div className="flex-1 overflow-x-auto scrollbar-hide">
                        <div className="flex items-center min-w-full h-[68px] px-2">
                            {[
                                ...mobileNavAll,
                                ...(user.is_admin ? [{ to: "/admin", key: "nav_admin", label: "Admin", icon: ShieldCheck }] : []),
                            ].map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.to.replace("/", "")}`}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center gap-1 shrink-0 basis-[68px] px-2 py-2 min-h-[60px] text-[12px] font-semibold leading-none rounded-xl transition-colors ${
                                            isActive
                                                ? "text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                                                : "text-[hsl(var(--muted-foreground))]"
                                        }`
                                    }
                                >
                                    <l.icon className="w-6 h-6 shrink-0" />
                                    <span className="truncate max-w-full text-center">{l.label || t(language, l.key)}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>

                    {/* Caesar spark — pinned right, always visible */}
                    <button
                        data-testid="mobile-nav-chat"
                        onClick={() => window.dispatchEvent(new CustomEvent("caesar:open"))}
                        aria-label={t(language, "nav_chat")}
                        title={t(language, "nav_chat")}
                        className="shrink-0 w-16 grid place-items-center border-l border-[hsl(var(--border))] bg-[hsl(var(--card))]"
                    >
                        <span
                            className="w-12 h-12 rounded-full text-white shadow-lg grid place-items-center active:scale-95 transition-transform ring-2 ring-[hsl(var(--background))]"
                            style={{
                                background: "linear-gradient(120deg, #0AFFB0 0%, #22D3EE 45%, #B36CFF 100%)",
                                boxShadow: "0 8px 24px -6px rgba(10,255,176,0.55), 0 4px 14px -6px rgba(179,108,255,0.55)",
                            }}
                        >
                            <AssistantSparkIcon size={26} stroke="#FFFFFF" />
                        </span>
                    </button>
                </div>
            </nav>

            {/* Floating Caesar chat widget — global */}
            <CaesarWidget />
        </div>
    );
};

export default AppLayout;
